import React, {useEffect, useState} from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
    FORMAT_TEXT_COMMAND,
    UNDO_COMMAND,
    REDO_COMMAND,
    $getSelection,
    $isRangeSelection,
    $createParagraphNode,
} from "lexical";
import {
    INSERT_ORDERED_LIST_COMMAND,
    INSERT_UNORDERED_LIST_COMMAND,
} from "@lexical/list";
import {
    FORMAT_ELEMENT_COMMAND,
    INDENT_CONTENT_COMMAND,
    OUTDENT_CONTENT_COMMAND,
} from "lexical";
import { $createQuoteNode, $createHeadingNode, $isQuoteNode } from "@lexical/rich-text";
import {Button, Col, Dropdown, Form, Input, Modal, Row, Space, Tooltip, message, Tabs} from "antd";
import {
    BiAlignLeft,
    BiAlignMiddle, BiAlignRight,
    BiBold, BiCode, BiCopy, BiHeading,
    BiItalic, BiLeftIndent, BiLink,
    BiListOl,
    BiListUl, BiParagraph,
    BiRedo, BiRightIndent, BiSolidMagicWand, BiSolidQuoteAltRight,
    BiStrikethrough,
    BiUnderline,
    BiUndo
} from "react-icons/bi";
import { $isHeadingNode } from "@lexical/rich-text";
import { $isListNode } from "@lexical/list";
import { TOGGLE_LINK_COMMAND, $isLinkNode } from "@lexical/link";
import { $createTextNode } from "lexical";
import { $createCodeNode, $isCodeNode } from "@lexical/code";
import {LuRemoveFormatting} from "react-icons/lu";
import { $isTextNode } from "lexical";
import { $patchStyleText } from "@lexical/selection";
import {GoFileCode} from "react-icons/go";
import { $generateHtmlFromNodes } from "@lexical/html";
import TurndownService from "turndown";


function BlockFormatDropdown({ currentBlock = "paragraph", onChange }) {
    const iconStyle = {
        marginBottom: '-2px',
        marginRight: '5px'
    }

    const blockItems = [
        { label: 'Paragraph', key: 'paragraph', value: 'paragraph', icon: <BiParagraph style={iconStyle} /> },
        { label: 'Heading 1', key: 'h1', value: 'h1', icon: <BiHeading style={iconStyle} /> },
        { label: 'Heading 2', key: 'h2', value: 'h2', icon: <BiHeading style={iconStyle} /> },
        { label: 'Heading 3', key: 'h3', value: 'h3', icon: <BiHeading style={iconStyle} /> },
        { label: 'Heading 4', key: 'h4', value: 'h4', icon: <BiHeading style={iconStyle} /> },
        { label: 'Heading 5', key: 'h5', value: 'h5', icon: <BiHeading style={iconStyle} /> },
        { label: 'Heading 6', key: 'h6', value: 'h6', icon: <BiHeading style={iconStyle} /> },
        { label: 'Code', key: 'code', value: 'code', icon: <BiCode style={iconStyle} /> },
        { label: 'Blockquote', key: 'quote', value: 'quote', icon: <BiSolidQuoteAltRight style={iconStyle} /> },
    ];

    const handleMenuClick = ({ key }) => {
        if (onChange) {
            onChange(key);
        }
    };

    const selectedItem = blockItems.find(i => i.key === currentBlock);

    return <Tooltip title={selectedItem?.label || "Paragraph"}>
        <Dropdown menu={{items: blockItems, onClick: handleMenuClick}} trigger={["click"]}>
            <Button style={{width: '115px', minWidth: '115px'}}>
                {selectedItem?.icon} {selectedItem?.label || "Paragraph"}
            </Button>
        </Dropdown>
    </Tooltip>;
}

export default function EditorToolbar({ selectedText, showMagicRewrite = false, showViewSource = true }) {
    const [editor] = useLexicalComposerContext();
    const [blockType, setBlockType] = useState("paragraph");
    const [isBold, setIsBold] = useState(false);
    const [isItalic, setIsItalic] = useState(false);
    const [isUnderline, setIsUnderline] = useState(false);
    const [isStrikethrough, setIsStrikethrough] = useState(false);
    const [isLink, setIsLink] = useState(false);
    const [isOlList, setIsOlList] = useState(false);
    const [isUlList, setIsUlList] = useState(false);
    const [showLinkModal, setShowLinkModal] = useState(false);
    const [linkValue, setLinkValue] = useState('');
    const [showSourceModal, setShowSourceModal] = useState(false);
    const [htmlSource, setHtmlSource] = useState(false);
    const [markdownSource, setMarkdownSource] = useState(false);

    useEffect(() => {
        return editor.registerUpdateListener(({ editorState }) => {
            editorState.read(() => {
                const selection = $getSelection();
                if ($isRangeSelection(selection)) {
                    const anchorNode = selection.anchor.getNode();
                    const topLevelNode = anchorNode.getTopLevelElementOrThrow();

                    setIsBold(selection.hasFormat("bold"));
                    setIsItalic(selection.hasFormat("italic"));
                    setIsUnderline(selection.hasFormat("underline"));
                    setIsStrikethrough(selection.hasFormat("strikethrough"));

                    let node = selection.anchor.getNode();
                    let linkNode = null;
                    let olListNode = null;
                    let ulListNode = null;

                    while (node !== null) {
                        if ($isLinkNode(node)) {
                            linkNode = node;
                            break;
                        } else if($isListNode(node) && node.getTag() === "ol") {
                            olListNode = node;
                            break;
                        } else if ($isListNode(node) && node.getTag() === "ul") {
                            ulListNode = node;
                            break;
                        }
                        node = node.getParent();
                    }

                    if (linkNode) {
                        setIsLink(true);
                    } else {
                        setIsLink(false);
                    }

                    if(olListNode) {
                        setIsOlList(true);
                    } else {
                        setIsOlList(false);
                    }

                    if(ulListNode) {
                        setIsUlList(true);
                    } else {
                        setIsUlList(false);
                    }

                    if (topLevelNode.getType() === "paragraph") {
                        setBlockType("paragraph");
                    } else if ($isHeadingNode(topLevelNode)) {
                        setBlockType(topLevelNode.getTag()); // h1, h2, h3
                    } else if ($isListNode(topLevelNode)) {
                        setBlockType(topLevelNode.getListType()); // bullet, number, check
                    } else if (topLevelNode.getType() === "quote") {
                        setBlockType("quote");
                    } else if (topLevelNode.getType() === "code") {
                        setBlockType("code");
                    } else {
                        setBlockType(topLevelNode.getType());
                    }
                }
            });
        });
    }, [editor]);

    const applyBlock = (type) => {
        editor.update(() => {
            const selection = $getSelection();
            if (!$isRangeSelection(selection)) return;

            const nodes = selection.getNodes();
            if (nodes.length === 0) return;

            const topLevelNodes = Array.from(
                new Set(nodes.map((n) => n.getTopLevelElementOrThrow()))
            );

            if (topLevelNodes.length === 0) return;

            if (type === "code") {
                if (topLevelNodes.length === 1 && topLevelNodes[0].getType() === "code") {
                    const codeNode = topLevelNodes[0];
                    const textContent = codeNode.getTextContent();

                    const lines = textContent.split("\n");

                    const paragraphNodes = lines
                        .map((line) => {
                            if (line.trim().length === 0) return null; // ✅ skip empty lines
                            const p = $createParagraphNode();
                            p.append($createTextNode(line));
                            return p;
                        })
                        .filter(Boolean);

                    if (paragraphNodes.length > 0) {
                        codeNode.insertBefore(paragraphNodes[0]);
                        for (let i = 1; i < paragraphNodes.length; i++) {
                            paragraphNodes[i - 1].insertAfter(paragraphNodes[i]);
                        }
                    }

                    codeNode.remove();
                    return;
                }

                const firstNode = topLevelNodes[0];
                const codeNode = $createCodeNode();
                firstNode.insertBefore(codeNode);

                const lines = topLevelNodes.map((n) => n.getTextContent());
                const joined = lines.join("\n");

                codeNode.append($createTextNode(joined));

                topLevelNodes.forEach((n) => n.remove());
                return;
            }

            if (type === "quote") {
                const firstNode = topLevelNodes[0];
                const quoteNode = $createQuoteNode();
                firstNode.insertBefore(quoteNode);

                const lines = topLevelNodes.map((n) => n.getTextContent());
                const joined = lines.join("\n");
                quoteNode.append($createTextNode(joined));

                topLevelNodes.forEach((n) => n.remove());
                return;
            }

            if (type === "paragraph") {
                topLevelNodes.forEach((topNode) => {
                    if ($isQuoteNode(topNode) || $isCodeNode(topNode)) {
                        const textContent = topNode.getTextContent();
                        const lines = textContent.split("\n");

                        const paragraphNodes = lines
                            .map((line) => {
                                if (line.trim().length === 0) return null;
                                const p = $createParagraphNode();
                                p.append($createTextNode(line));
                                return p;
                            })
                            .filter(Boolean);

                        if (paragraphNodes.length > 0) {
                            topNode.insertBefore(paragraphNodes[0]);
                            for (let i = 1; i < paragraphNodes.length; i++) {
                                paragraphNodes[i - 1].insertAfter(paragraphNodes[i]);
                            }
                        }
                        topNode.remove();
                    } else if (!topNode.is("paragraph")) {
                        const p = $createParagraphNode().append(...topNode.getChildren());
                        topNode.replace(p);
                    }
                });
                return;
            }

            topLevelNodes.forEach((topNode) => {
                if (type === "paragraph" && !topNode.is("paragraph")) {
                    const p = $createParagraphNode().append(...topNode.getChildren());
                    topNode.replace(p);
                } else if (type === "quote" && !topNode.is("quote")) {
                    const q = $createQuoteNode().append(...topNode.getChildren());
                    topNode.replace(q);
                } else if (
                    ["h1", "h2", "h3", "h4", "h5", "h6"].includes(type) &&
                    (!topNode.is("heading") || topNode.getTag() !== type)
                ) {
                    const h = $createHeadingNode(type).append(...topNode.getChildren());
                    topNode.replace(h);
                }
            });
        });
    };

    const toggleLink = () => {
        editor.update(() => {
            const selection = $getSelection();
            if ($isRangeSelection(selection)) {
                const anchorNode = selection.anchor.getNode();

                if ($isLinkNode(anchorNode.getParent())) {
                    editor.dispatchCommand(TOGGLE_LINK_COMMAND, null); // remove
                } else {
                    setShowLinkModal(true);
                }
            }
        });
    };

    const applyLink = () => {
        editor.update(() => {
            const selection = $getSelection();
            if ($isRangeSelection(selection)) {
                editor.dispatchCommand(TOGGLE_LINK_COMMAND, linkValue);
                setLinkValue('');
            }
        });
    };

    const removeFormatting = () => {
        editor.update(() => {
            const selection = $getSelection();
            if (!selection) return;

            if (!$isRangeSelection(selection)) return;

            if (selection.isCollapsed()) {
                const anchor = selection.anchor.getNode();
                if ($isTextNode(anchor)) {
                    anchor.setFormat(0);
                    anchor.setStyle("");
                }
                selection.setFormat(0);
                editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
                return;
            }

            $patchStyleText(selection, {
                color: null,
                "background-color": null,
                "font-size": null,
                "font-family": null,
                "text-decoration": null,
                "font-weight": null,
                "font-style": null,
            });

            const nodes = selection.getNodes();
            for (const n of nodes) {
                if ($isTextNode(n)) {
                    n.setFormat(0);
                    n.setStyle("");
                } else if ($isLinkNode(n)) {
                    n.replace(n.getChildren());
                }
            }

            editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);

            const stillStyled = nodes.some((n) =>
                $isTextNode(n) ? (n.getFormat() !== 0 || n.getStyle() !== "") : false
            );
            if (stillStyled) {
                const plain = selection.getTextContent();
                selection.insertText(plain); // guaranteed plain text
            }
        });
    };

    const showSource = () => {
        editor.getEditorState().read(() => {
            const rawHtml = $generateHtmlFromNodes(editor);
            const doc = new DOMParser().parseFromString(rawHtml, "text/html");
            doc.querySelectorAll("span").forEach(span => {
                const parent = span.parentNode;
                while (span.firstChild) {
                    parent.insertBefore(span.firstChild, span);
                }
                parent.removeChild(span);
            });

            doc.querySelectorAll("[class]").forEach(el => {
                const classList = Array.from(el.classList);
                const filtered = classList.filter(cls => !cls.includes("editor-"));
                if (filtered.length > 0) {
                    el.className = filtered.join(" ");
                } else {
                    el.removeAttribute("class");
                }
            });

            doc.querySelectorAll("[style]").forEach(el => {
                const style = el.getAttribute("style");
                if (style.includes("white-space: pre-wrap")) {
                    const newStyle = style
                        .split(";")
                        .map(s => s.trim())
                        .filter(s => s && !s.startsWith("white-space"))
                        .join("; ");
                    if (newStyle) {
                        el.setAttribute("style", newStyle);
                    } else {
                        el.removeAttribute("style");
                    }
                }
            });

            const cleanHtml = doc.body.innerHTML.trim();

            const htmlFormatted = cleanHtml
                .replace(/></g, ">\n<")
                .split("\n")
                .map((line) => line.trim())
                .join("\n");

            const turndownService = new TurndownService();
            const markdownFormatted = turndownService.turndown(htmlFormatted);

            setHtmlSource(htmlFormatted);
            setMarkdownSource(markdownFormatted);

            setShowSourceModal(true);
        });
    };

    const { TextArea } = Input;

    const sourceTabs = [
        { label: 'HTML', key: 'html', children: <>
                <Row justify='space-between'>
                    <Col />
                    <Col>
                        <Button icon={<BiCopy style={{marginBottom: '-2px'}} />} onClick={() => {
                            navigator.clipboard.writeText(htmlSource);
                            message.success('Source copied to clipboard!');
                        }}>Copy</Button>
                    </Col>
                </Row>
                <br />
                <TextArea value={htmlSource} rows={20} />
            </> },
        { label: 'Markdown', key: 'markdown', children: <>
                <Row justify='space-between'>
                    <Col />
                    <Col>
                        <Button icon={<BiCopy style={{marginBottom: '-2px'}} />} onClick={() => {
                            navigator.clipboard.writeText(markdownSource);
                            message.success('Source copied to clipboard!');
                        }}>Copy</Button>
                    </Col>
                </Row>
                <br />
                <TextArea value={markdownSource} rows={20} />
            </> },
    ];

    return (
        <Row gutter={[16, 16]}>
            <Col>
                <Space>
                    {/* Blocks */}

                    <Space.Compact>
                        {showMagicRewrite ? <Tooltip title='Rewrite with AI'>
                            <Button onClick={() => {
                                editor.update(() => {
                                    const selection = $getSelection();
                                    if ($isRangeSelection(selection)) {
                                        selectedText(selection.getTextContent())
                                    }
                                })
                            }} icon={<BiSolidMagicWand style={{marginBottom: '-2px'}} />} />
                        </Tooltip> : <></>}

                        <BlockFormatDropdown currentBlock={blockType} onChange={(value) => applyBlock(value)} />

                        {/* Inline formatting */}
                        <Tooltip title='Bold'>
                            <Button type={isBold ? 'primary' : 'default'} onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold")} icon={<BiBold style={{marginBottom: '-2px'}} />} />
                        </Tooltip>
                        <Tooltip title='Italic'>
                            <Button type={isItalic ? 'primary' : 'default'} onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic")} icon={<BiItalic style={{marginBottom: '-2px'}} />} />
                        </Tooltip>
                        <Tooltip title='Underline'>
                            <Button type={isUnderline ? 'primary' : 'default'} onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "underline")} icon={<BiUnderline style={{marginBottom: '-2px'}} />} />
                        </Tooltip>
                        <Tooltip title='Strikethrough'>
                            <Button type={isStrikethrough ? 'primary' : 'default'} onClick={() =>
                                editor.update(() => {
                                    const selection = $getSelection();
                                    if ($isRangeSelection(selection)) {
                                        if (selection.isCollapsed()) {
                                            // toggle for caret
                                            editor.dispatchCommand(FORMAT_TEXT_COMMAND, "strikethrough");
                                        } else {
                                            // apply only to selected text
                                            selection.formatText("strikethrough");
                                        }
                                    }
                                })
                            } icon={<BiStrikethrough style={{marginBottom: '-2px'}} />} />
                        </Tooltip>

                        {/* Lists */}
                        <Tooltip title='Bulleted List'>
                            <Button type={isUlList ? 'primary' : 'default'} onClick={() => editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND)} icon={<BiListUl style={{marginBottom: '-2px'}} />} />
                        </Tooltip>
                        <Tooltip title='Numbered List'>
                            <Button type={isOlList ? 'primary' : 'default'} onClick={() => editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND)} icon={<BiListOl style={{marginBottom: '-2px'}} />} />
                        </Tooltip>

                        {/* Link */}
                        <Tooltip title='Link'>
                            <Button type={isLink ? 'primary' : 'default'} onClick={toggleLink} icon={<BiLink style={{marginBottom: '-2px'}} />} />
                        </Tooltip>

                        {/* Alignment / Indent */}
                        <Tooltip title='Left Align'>
                            <Button onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "left")} icon={<BiAlignLeft style={{marginBottom: '-2px'}} />} />
                        </Tooltip>
                        <Tooltip title='Center Align'>
                            <Button onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "center")} icon={<BiAlignMiddle style={{marginBottom: '-2px'}} />} />
                        </Tooltip>
                        <Tooltip title='Right Align'>
                            <Button onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "right")} icon={<BiAlignRight style={{marginBottom: '-2px'}} />} />
                        </Tooltip>

                        <Tooltip title='Increase Indent'>
                            <Button onClick={() => editor.dispatchCommand(INDENT_CONTENT_COMMAND)} icon={<BiRightIndent style={{marginBottom: '-2px'}} />} />
                        </Tooltip>
                        <Tooltip title='Decrease Indent'>
                            <Button onClick={() => editor.dispatchCommand(OUTDENT_CONTENT_COMMAND)} icon={<BiLeftIndent style={{marginBottom: '-2px'}} />} />
                        </Tooltip>

                        {/* Undo/Redo */}
                        <Tooltip title='Undo'>
                            <Button onClick={() => editor.dispatchCommand(UNDO_COMMAND)} icon={<BiUndo style={{marginBottom: '-2px'}} />} />
                        </Tooltip>
                        <Tooltip title='Redo'>
                            <Button onClick={() => editor.dispatchCommand(REDO_COMMAND)} icon={<BiRedo style={{marginBottom: '-2px'}} />} />
                        </Tooltip>
                        {/* Clear formatting */}
                        <Tooltip title='Clear Formatting'>
                            <Button onClick={removeFormatting} icon={<LuRemoveFormatting style={{marginBottom: '-2px'}} />} />
                        </Tooltip>
                        {/* View source */}
                        {showViewSource ? <Tooltip title='View Source'>
                            <Button onClick={showSource} icon={<GoFileCode style={{marginBottom: '-2px'}} />} />
                        </Tooltip> : <></>}
                    </Space.Compact>
                </Space>
            </Col>

            <Modal
                open={showLinkModal}
                okText='Apply'
                onOk={() => {
                    if(linkValue.trim() === '') {
                        return message.error('Please enter a valid URL!');
                    }
                    applyLink();
                    setShowLinkModal(false);
                }}
                onCancel={() => {
                    setShowLinkModal(false);
                    setLinkValue('');
                }}
                title='Add URL'>
                <Form layout='vertical'>
                    <Form.Item label='Enter URL:'>
                        <Input placeholder='Enter URL' onChange={(e) => setLinkValue(e.target.value)} />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                open={showSourceModal}
                onCancel={() => setShowSourceModal(false)}
                footer={null}
                width={800}
                title='View Source'>
                <Tabs items={sourceTabs} />
            </Modal>
        </Row>
    );
}
